import { CircadianPhase, TaskItem } from '@demind-inc/core';
import { FC, MouseEvent, useMemo, useState, useCallback, useEffect } from 'react';
import { useDrag } from 'react-dnd';
import { RepeatOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import './KanbanTaskCard.scss';
import { getCssVariable } from '../../../utils';
import { AddTodoTaskItemProps, circadianPhaseInfo } from '../../../data-access';
import { useGeneralSettings, useTaskSettings, useTaskTimeSuggestion } from '../../../hooks';
import {
  formatTaskTimeRangeFromDuration,
  getDueDateYYYYMMDD,
  getFormattedOverdueTime,
} from '../../../helpers';
import { translateMinToHHhMMmString } from '../../../data-access/helpers/date.helpers';
import { TODO_INTEGRATION_ICONS } from '../../TodoIntegration';
import { TaskCheckBox, SubTaskProgress } from '../../common/';
import { SubTasks } from '../SubTasks';

import { CircularProgress, Typography } from '@mui/material';
import { getPriorityColor } from '../../Priority/helpers';
import { ReactComponent as SparkleIcon } from '../../../assets/scheduler/sparkles_white.svg';
import { isAppleRemindersDesktopSupported } from '../../../helpers/appleRemindersDesktop.helpers';

interface KanbanTaskCardProps {
  task: TaskItem;
  phase?: CircadianPhase;
  projectName?: string;
  isOverDue?: boolean;
  onTaskComplete?: (taskId: string, completed: boolean, parentOriginalTaskId?: string) => void;
  onTaskUpdate?: (
    taskId: string,
    boardId: string,
    updates: Partial<TaskItem>,
    shouldReflectToEvent?: boolean
  ) => Promise<void>;
  onClick?: () => void;
  onTaskCreate?: (options: AddTodoTaskItemProps) => Promise<void>;
  onTaskDelete?: (taskId: string, boardId: string, parentOriginalTaskId?: string) => void;
  className?: string;
}

const taskIncompleteColor = getCssVariable('--color-gray-base') || '#b5b5bc';
const taskCompleteColor = getCssVariable('--color-primary');
const primaryLightColor = getCssVariable('--color-primary-light') || '#680ddb29';
const primaryColor = getCssVariable('--color-primary') || '#680ddb';
const TASK_EXPANDED_LABEL_COUNT = 1;

const KanbanTaskCard: FC<KanbanTaskCardProps> = ({
  task,
  projectName,
  phase,
  isOverDue = false,
  onTaskComplete = () => void 0,
  onTaskUpdate = async () => Promise.resolve(),
  onClick = () => void 0,
  onTaskCreate = () => void 0,
  onTaskDelete = () => void 0,
  className,
}) => {
  const { t } = useTranslation();
  const safeLabels = Array.isArray(task.labels) ? task.labels : [];
  const [isExpanded, setIsExpanded] = useState(false);
  const [localSubTasks, setLocalSubTasks] = useState<TaskItem[]>(task.subTasks || []);
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const {
    taskSettings: { durationMin: taskDefaultDurationMin },
  } = useTaskSettings();

  // Sync local subtasks with task prop
  useEffect(() => {
    setLocalSubTasks(task.subTasks || []);
  }, [task.subTasks]);
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'TASK',
      item: task,
      canDrag: () => !task.completed,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [task]
  );

  const { taskDurationStr, taskTime } = useMemo(() => {
    const taskDurationStr = task.duration?.amount
      ? translateMinToHHhMMmString(task.duration.amount)
      : `${taskDefaultDurationMin}m`;

    const taskTime = formatTaskTimeRangeFromDuration({
      duration: task.duration || {
        amount: taskDefaultDurationMin,
        unit: 'minute',
      },
      dueDateTime: task.dueDateTime,
      timeFormat,
    });

    return { taskDurationStr, taskTime };
  }, [task, taskDefaultDurationMin]);

  const hasSubTasks = useMemo(() => {
    return localSubTasks.length > 0;
  }, [localSubTasks]);

  const subtasksCount = useMemo(() => {
    return localSubTasks.length;
  }, [localSubTasks]);

  const completedSubTasksCount = useMemo(() => {
    return localSubTasks.filter((t) => t.completed).length;
  }, [localSubTasks]);

  const pieChartData = useMemo(() => {
    const completed = completedSubTasksCount;
    const remaining = subtasksCount - completedSubTasksCount;

    const data = [];
    if (completed > 0) {
      data.push({
        id: 0,
        value: completed,
        color: primaryColor,
      });
    }
    if (remaining > 0) {
      data.push({
        id: 1,
        value: remaining,
        color: primaryLightColor,
      });
    }
    if (data.length === 0) {
      data.push({
        id: 0,
        value: 1,
        color: primaryLightColor,
      });
    }
    return data;
  }, [completedSubTasksCount, subtasksCount]);

  const handleTaskComplete = useCallback(
    (taskId: string, completed: boolean, parentOriginalTaskId?: string) => {
      // Update local state optimistically
      setLocalSubTasks((prev) =>
        prev.map((subtask) => (subtask.taskId === taskId ? { ...subtask, completed } : subtask))
      );
      onTaskComplete(taskId, completed, parentOriginalTaskId);
    },
    [onTaskComplete]
  );

  const handleUpdateSubTask = useCallback(
    (taskId: string, updates: Partial<TaskItem>) => {
      // Update local state optimistically
      setLocalSubTasks((prev) =>
        prev.map((subtask) => (subtask.taskId === taskId ? { ...subtask, ...updates } : subtask))
      );
      onTaskUpdate(taskId, task.boardId, updates, false);
    },
    [onTaskUpdate, task.boardId]
  );

  const handleCreateSubTask = useCallback(
    async (options: AddTodoTaskItemProps) => {
      // Add to local state optimistically with temporary taskId
      const tempTaskId = `temp-${Date.now()}`;
      const newSubTask: TaskItem = {
        ...options,
        taskId: tempTaskId,
        boardId: task.boardId,
      } as TaskItem;
      setLocalSubTasks((prev) => [...prev, newSubTask]);
      try {
        await onTaskCreate(options);
      } catch (error) {
        // Revert on error
        setLocalSubTasks((prev) => prev.filter((st) => st.taskId !== tempTaskId));
        throw error;
      }
    },
    [onTaskCreate, task.boardId]
  );

  const handleDeleteSubTask = useCallback(
    (taskId: string, boardId: string, parentOriginalTaskId?: string) => {
      // Update local state optimistically
      setLocalSubTasks((prev) => prev.filter((subtask) => subtask.taskId !== taskId));
      onTaskDelete(taskId, boardId, parentOriginalTaskId);
    },
    [onTaskDelete]
  );

  const phaseColor = useMemo(() => {
    return phase ? (circadianPhaseInfo[phase]?.color ?? taskIncompleteColor) : taskIncompleteColor;
  }, [phase]);

  const suggestionDate = useMemo(() => {
    return getDueDateYYYYMMDD(task?.dueDateTime) || dayjs().format('YYYY-MM-DD');
  }, [task?.dueDateTime]);

  const { requestSuggestion, suggestionKey, taskSuggestionMap } = useTaskTimeSuggestion({
    date: suggestionDate,
    task: {
      ...task,
      duration: task.duration || { amount: taskDefaultDurationMin, unit: 'minute' },
      dueDateTime: {
        ...(task.dueDateTime ?? {}),
        date: suggestionDate,
        timezone: task.dueDateTime?.timezone ?? dayjs.tz.guess(),
      },
    },
    suggestionKey: (task as any)?.taskId ?? (task as any)?.originalTaskId,
  });
  const isSuggestingTime = taskSuggestionMap[suggestionKey]?.status === 'pending';

  const handleSuggestTime = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (
        (task.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) ||
        task.completed
      )
        return;

      await requestSuggestion(async (suggestedTime) => {
        const taskTz = task.dueDateTime?.timezone ?? dayjs.tz.guess();
        await onTaskUpdate(
          task.taskId,
          task.boardId,
          {
            ...task,
            dueDateTime: {
              ...(task.dueDateTime ?? {}),
              timezone: taskTz,
              datetime: suggestedTime.toISOString(),
              date: dayjs(suggestedTime).tz(taskTz).format('YYYY-MM-DD'),
            },
            addedToCalendar: true,
          },
          true
        );
      });
    },
    [onTaskUpdate, requestSuggestion, task]
  );

  return (
    <div
      ref={drag}
      style={{ opacity: isDragging ? 0.5 : task.completed ? 0.6 : 1 }}
      className={clsx('kanban-task-card', className)}
      onClick={onClick}
      key={task.taskId}
    >
      <div className="kanban-task-card__top-group">
        <div className="kanban-task-card__top-group__time">
          {isOverDue ? (
            <Typography variant="caption" className="kanban-task-card__top-group__overdue">
              {getFormattedOverdueTime(task, timeFormat)}
            </Typography>
          ) : (
            <>
              {taskTime ? (
                <Typography
                  variant="caption"
                  className={clsx('kanban-task-card__time')}
                  style={{ color: phaseColor, borderColor: phaseColor }}
                >
                  {taskTime}
                </Typography>
              ) : (
                <button
                  type="button"
                  className={clsx(
                    'kanban-task-card__time',
                    isSuggestingTime
                      ? 'kanban-task-card__time--loading'
                      : 'kanban-task-card__time--ai'
                  )}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={handleSuggestTime}
                  disabled={
                    (task.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) ||
                    task.completed ||
                    isSuggestingTime ||
                    !onTaskUpdate
                  }
                  aria-label={t('task.card.smartSchedule')}
                >
                  <SparkleIcon className="kanban-task-card__time__sparkle" />
                  {isSuggestingTime ? (
                    <CircularProgress size={12} className="kanban-task-card__time__loader" />
                  ) : (
                    <span className="kanban-task-card__time__label">
                      {t('task.card.smartSchedule')}
                    </span>
                  )}
                </button>
              )}
            </>
          )}
          {taskDurationStr && (
            <Typography className="kanban-task-card__phase" variant="caption">
              {taskDurationStr}
            </Typography>
          )}
          {task.recurring?.isRecurring && (
            <span className="kanban-task-card__repeat-icon--container">
              <RepeatOutlined className="kanban-task-card__repeat-icon" />
            </span>
          )}
        </div>
        <div className="kanban-task-card__project">
          <img
            src={TODO_INTEGRATION_ICONS[task.appFrom]}
            alt={task.appFrom}
            className="kanban-task-card__project__icon"
          />
          <Typography variant="caption" className="kanban-task-card__project__text">
            {projectName}
          </Typography>
        </div>
      </div>
      <div className="kanban-task-card__bottom-group">
        <TaskCheckBox
          isCompleted={task.completed}
          onChange={(checked) => handleTaskComplete(task.taskId, checked)}
          size={20}
          svgRatio={1}
          color={
            task.priority && task.priority !== 'none'
              ? getPriorityColor(task.priority)
              : task.completed
                ? taskCompleteColor
                : taskIncompleteColor
          }
          hasPriority={!!task.priority && task.priority !== 'none'}
          className="kanban-task-card__bottom-group__status"
          disabled={
            (task.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) ||
            (task.recurring?.isRecurring === true && task.completed)
          }
        />
        <div className="kanban-task-card__content">
          <Typography
            variant="body2"
            className={clsx('kanban-task-card__title', {
              'kanban-task-card__title--done': task.completed,
            })}
          >
            {task.name}
          </Typography>
          <div className="kanban-task-card__bottom-row">
            <div className="kanban-task-card__labels">
              {!!safeLabels.length && (
                <>
                  {safeLabels.slice(0, TASK_EXPANDED_LABEL_COUNT).map((label) => (
                    <div
                      key={`${task.taskId}-${label.name}`}
                      className="kanban-task-card__labels__item"
                      id={label.name}
                    >
                      <div
                        className="kanban-task-card__labels__item__dot"
                        style={{ backgroundColor: label.color || primaryColor }}
                      />
                      <span className="kanban-task-card__labels__item__name">
                        {label.name || t('task.labels.noName')}
                      </span>
                    </div>
                  ))}
                  {safeLabels.length > TASK_EXPANDED_LABEL_COUNT && (
                    <div className="kanban-task-card__labels__item">
                      <span className="kanban-task-card__labels__item__name">
                        +{safeLabels.length - TASK_EXPANDED_LABEL_COUNT}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
            {hasSubTasks && (
              <SubTaskProgress
                data={pieChartData}
                completedCount={completedSubTasksCount}
                totalCount={subtasksCount}
                onClick={() => setIsExpanded(!isExpanded)}
                isExpanded={isExpanded}
              />
            )}
          </div>
        </div>
      </div>
      {isExpanded && hasSubTasks && (
        <SubTasks
          parentTask={task}
          subtasks={localSubTasks}
          canEdit={task.appFrom !== 'apple-reminders' && task.appFrom !== 'notion'}
          onCompleteSubTask={(taskId, completed) =>
            handleTaskComplete(taskId, completed, task.originalTaskId)
          }
          onCreateSubTask={handleCreateSubTask}
          onUpdateSubTask={handleUpdateSubTask}
          onDeleteSubTask={(taskId) =>
            handleDeleteSubTask(taskId, task.boardId, task.originalTaskId)
          }
          disableDrag
        />
      )}
    </div>
  );
};

export default KanbanTaskCard;
